import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { useBookingStore } from '../store/bookingStore';
import { MapPin, Calendar, Users, Repeat, Check, Receipt } from 'lucide-react';
import { useLanguage } from '../locales/LanguageContext';
import { CaptchaField } from '../components/CaptchaField';
import { toast } from 'sonner';

// Parsea "YYYY-MM-DD" como fecha LOCAL (sin conversión a UTC).
// new Date("YYYY-MM-DD") interpreta el string como UTC midnight, lo que
// adelanta un día en timezones negativos (GMT-3..GMT-6, toda América Latina).
const parseDateLocal = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const Step3_Confirmation = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const selectedVehicle = useBookingStore(state => state.selectedVehicle);
  const fromLabel = useBookingStore(state => state.fromLabel);
  const toLabel = useBookingStore(state => state.toLabel);
  const pickupAt = useBookingStore(state => state.pickupAt);
  const pickupTime = useBookingStore(state => state.pickupTime);
  const passengers = useBookingStore(state => state.passengers);
  const isRoundtrip = useBookingStore(state => state.isRoundtrip);
  const returnAt = useBookingStore(state => state.returnAt);
  const returnTime = useBookingStore(state => state.returnTime);
  const customerInfo = useBookingStore(state => state.customerInfo);
  const selectedAddons = useBookingStore(state => state.selectedAddons);
  const notes = useBookingStore(state => state.notes);
  const flightNumber = useBookingStore(state => state.flightNumber);
  const luggageCount = useBookingStore(state => state.luggageCount);
  const pickupAddress = useBookingStore(state => state.pickupAddress);
  const pickupExactAddress = useBookingStore(state => state.pickupExactAddress);
  const dropoffExactAddress = useBookingStore(state => state.dropoffExactAddress);
  const reservationCreated = useBookingStore(state => state.reservationCreated);
  const nextStep = useBookingStore(state => state.nextStep);
  const setCaptchaToken = useBookingStore(state => state.setCaptchaToken);
  const [localCaptchaToken, setLocalCaptchaToken] = useState(null);
  const hasSitekey = !!process.env.REACT_APP_HCAPTCHA_SITEKEY;

  useEffect(() => {
    if (!selectedVehicle || !customerInfo.email) {
      navigate('/booking');
    }
  }, [selectedVehicle, customerInfo, navigate]);
  
  const calculateTotal = () => {
    // Si ya existe reserva creada en el backend, usar ese total (fuente de verdad)
    if (reservationCreated && reservationCreated.total != null) {
      return Number(reservationCreated.total).toFixed(2);
    }
    // Fallback: cálculo local (solo antes de crear reserva)
    let total = selectedVehicle?.price || 0;
    selectedAddons.forEach(addon => {
      if (addon.type === 'PER_PERSON') {
        total += addon.price * addon.quantity * passengers;
      } else {
        total += addon.price * addon.quantity;
      }
    });
    return total.toFixed(2);
  };
  
  const handleCaptchaVerify = (token) => {
    setLocalCaptchaToken(token);
    setCaptchaToken(token);
  };

  const handleNext = () => {
    if (hasSitekey && !localCaptchaToken) {
      toast.error(t('step3.captcha_required') || 'Por favor, complete el captcha antes de continuar.');
      return;
    }
    nextStep();
    navigate('/booking/payment');
  };
  
  if (!selectedVehicle) return null;
  
  return (
    <WizardLayout 
      onNext={handleNext}
      canGoNext={true}
    >
      <div data-testid="step-confirmation">
        <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-navy-950 mb-2">
          {t('step3.title')}
        </h2>
        <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
          {t('step3.subtitle')}
        </p>
        
        <div className="space-y-4 sm:space-y-6">
          {/* Trip Details */}
          <div className="bg-indigo-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h3 className="font-manrope font-semibold text-base sm:text-lg text-navy-900 mb-3 sm:mb-4">
              {t('step3.trip_details')}
            </h3>

            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex items-start gap-2.5 sm:gap-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-navy-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-slate-600">{t('step3.from')}</p>
                  <p className="font-medium text-sm sm:text-base text-slate-900">{fromLabel}</p>
                  {pickupExactAddress && (
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{pickupExactAddress}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2.5 sm:gap-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-navy-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-slate-600">{t('step3.to')}</p>
                  <p className="font-medium text-sm sm:text-base text-slate-900">{toLabel}</p>
                  {dropoffExactAddress && (
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{dropoffExactAddress}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2.5 sm:gap-3">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-navy-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-slate-600">{t('step3.pickup_datetime')}</p>
                  <p className="font-medium text-sm sm:text-base text-slate-900">
                    {pickupAt ? parseDateLocal(pickupAt).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : ''} {t('step3.at_time')} {pickupTime}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 sm:gap-3">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-navy-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-slate-600">{t('step3.passengers')}</p>
                  <p className="font-medium text-sm sm:text-base text-slate-900">{passengers} {passengers === 1 ? t('step3.passenger_singular') : t('step3.passengers_plural')}</p>
                </div>
              </div>

              {isRoundtrip && (
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <Repeat className="w-4 h-4 sm:w-5 sm:h-5 text-navy-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600">{t('step3.return_roundtrip')}</p>
                    <p className="font-medium text-sm sm:text-base text-slate-900">
                      {returnAt ? parseDateLocal(returnAt).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) + ` ${t('step3.at_time')} ${returnTime}` : t('step3.date_not_specified')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Vehicle */}
          <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h3 className="font-manrope font-semibold text-base sm:text-lg text-navy-900 mb-3 sm:mb-4">
              {t('step3.selected_vehicle')}
            </h3>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <img 
                src={selectedVehicle.image_url} 
                alt={selectedVehicle.name}
                className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0"
              />
              <div className="flex-1">
                <p className="font-semibold text-base sm:text-lg text-slate-900">{selectedVehicle.name}</p>
                <p className="text-xs sm:text-sm text-slate-600">{t('step3.capacity')}: {selectedVehicle.capacity_max} {t('step3.passengers_plural')}</p>
              </div>
              <div className="text-left sm:text-right w-full sm:w-auto">
                <p className="text-xl sm:text-2xl font-bold text-navy-800">
                  ${(reservationCreated?.base_price != null ? Number(reservationCreated.base_price) : (selectedVehicle.price || 0)).toFixed(2)}
                </p>
                <p className="text-xs sm:text-sm text-slate-500">USD</p>
              </div>
            </div>
          </div>
          
          {/* Customer Info */}
          <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h3 className="font-manrope font-semibold text-base sm:text-lg text-navy-900 mb-3 sm:mb-4">
              {t('step3.customer_info')}
            </h3>

            <div className="space-y-2 text-sm sm:text-base">
              <p><span className="text-slate-600">{t('step3.name_label')}</span> <span className="font-medium break-words">{customerInfo.full_name}</span></p>
              <p><span className="text-slate-600">{t('step3.email_label')}</span> <span className="font-medium break-all">{customerInfo.email}</span></p>
              <p><span className="text-slate-600">{t('step3.phone_label')}</span> <span className="font-medium">{customerInfo.phone}</span></p>
              {flightNumber && (
                <p><span className="text-slate-600">{t('step3.flight_number_label')}</span> <span className="font-medium">{flightNumber}</span></p>
              )}
              <p><span className="text-slate-600">{t('step3.luggage_count_label')}</span> <span className="font-medium">{luggageCount}</span></p>
              {pickupExactAddress && (
                <p><span className="text-slate-600">{t('step3.pickup_exact_label')}</span> <span className="font-medium break-words">{pickupExactAddress}</span></p>
              )}
              {dropoffExactAddress && (
                <p><span className="text-slate-600">{t('step3.dropoff_exact_label')}</span> <span className="font-medium break-words">{dropoffExactAddress}</span></p>
              )}
              {notes && (
                <p><span className="text-slate-600">{t('step3.notes_label')}</span> <span className="font-medium break-words">{notes}</span></p>
              )}
            </div>
          </div>
          
          {/* Price Breakdown */}
          <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h3 className="font-manrope font-semibold text-base sm:text-lg text-navy-900 mb-3 sm:mb-4 flex items-center gap-2">
              <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-navy-700" />
              {t('step3.price_breakdown')}
            </h3>

            <div className="space-y-2.5 text-sm sm:text-base">
              {/* Base transfer price */}
              <div className="flex justify-between items-center gap-2">
                <p className="text-slate-700 flex-1">
                  {t('step3.transfer_base_price')}
                  {isRoundtrip && (
                    <span className="text-xs ml-1 text-navy-600">({t('step3.roundtrip_label')})</span>
                  )}
                </p>
                <p className="font-semibold text-slate-900 whitespace-nowrap">
                  ${(reservationCreated?.base_price != null ? Number(reservationCreated.base_price) : (selectedVehicle?.price || 0)).toFixed(2)}
                </p>
              </div>

              {/* Each addon */}
              {selectedAddons.map((addon, idx) => {
                const addonTotal = addon.type === 'PER_PERSON'
                  ? addon.price * addon.quantity * passengers
                  : addon.price * addon.quantity;
                return (
                  <div key={idx} className="flex justify-between items-center gap-2">
                    <p className="text-slate-700 break-words flex-1">
                      {addon.name} × {addon.quantity}
                      {addon.type === 'PER_PERSON' ? ` × ${passengers} ${t('step3.passengers_plural')}` : ''}
                    </p>
                    <p className="font-semibold text-slate-900 whitespace-nowrap">
                      ${addonTotal.toFixed(2)}
                    </p>
                  </div>
                );
              })}

              {/* Separator + Total */}
              <div className="border-t border-slate-300 pt-2.5 mt-2.5">
                <div className="flex justify-between items-center gap-2">
                  <p className="font-bold text-navy-900 text-base sm:text-lg">{t('step3.total_to_pay')}</p>
                  <p className="font-bold text-navy-900 text-lg sm:text-xl">${calculateTotal()} USD</p>
                </div>
              </div>
            </div>
          </div>

          {/* Captcha */}
          <CaptchaField onVerify={handleCaptchaVerify} />

          {/* Guarantees */}
          <div className="bg-navy-800 text-white rounded-lg sm:rounded-xl p-4 sm:p-6">
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>{t('step3.free_cancellation')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>{t('step3.professional_driver')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>{t('step3.no_hidden_fees')}</span>
              </div>
            </div>
          </div>
          
          {/* Policies */}
          <div className="text-xs sm:text-sm text-slate-600 bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h4 className="font-semibold text-sm sm:text-base text-slate-900 mb-2">{t('step3.cancellation_policies')}</h4>
            <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm">
              <li>{t('step3.policy_1')}</li>
              <li>{t('step3.policy_2')}</li>
              <li>{t('step3.policy_3')}</li>
            </ul>
          </div>
        </div>
      </div>
    </WizardLayout>
  );
};
